import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';
import { Download, RefreshCw, Loader2 } from 'lucide-react';
import { logError } from '../../lib/logError';

const ExportDataPanel = ({ pin }) => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/sources?pin=${encodeURIComponent(pin)}`
      );
      if (res.ok) {
        const d = await res.json();
        setSources(d.sources || []);
      }
    } catch (e) {
      logError('[export] impossibile caricare le sorgenti', e);
    } finally {
      setLoading(false);
    }
  }, [pin]);

  useEffect(() => {
    if (pin) load();
  }, [pin, load]);

  const downloadCsv = async (src) => {
    setBusyKey(src.source);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/csv/${src.source}?pin=${encodeURIComponent(pin)}`;
      const res = await fetch(url);
      if (!res.ok) {
        toast({ title: 'Errore', description: `Impossibile scaricare ${src.label}`, variant: 'destructive' });
        return;
      }
      const blob = await res.blob();
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
      const filename = `${src.filename}_${ts}.csv`;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast({ title: 'Download avviato', description: `${src.label} (${src.count} record)` });
    } catch (e) {
      toast({ title: 'Errore di rete', description: String(e), variant: 'destructive' });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-800 p-4" style={{ backgroundColor: 'var(--tp-dark)' }}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-white text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
            <Download size={16} className="text-red-500" />
            Esportazione Dati per Sezione
          </h3>
          <p className="text-gray-500 text-xs mt-0.5">
            Un download CSV indipendente per ciascuna tabella del database.
          </p>
        </div>
        <Button
          onClick={load}
          size="sm"
          variant="outline"
          className="border-gray-700 text-white hover:bg-gray-800"
          data-testid="export-refresh-btn"
        >
          <RefreshCw size={13} />
        </Button>
      </div>

      {loading && sources.length === 0 ? (
        <div className="text-center py-6"><Loader2 size={20} className="animate-spin text-red-500 mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {sources.map((s) => (
            <Button
              key={s.source}
              onClick={() => downloadCsv(s)}
              disabled={busyKey === s.source}
              data-testid={`export-csv-${s.source}`}
              className="justify-between bg-gray-800 hover:bg-red-600 hover:border-red-500 text-white border border-gray-700 transition-colors h-auto py-2.5 px-3"
            >
              <span className="flex items-center gap-2 text-left">
                {busyKey === s.source ? (
                  <Loader2 size={14} className="animate-spin shrink-0" />
                ) : (
                  <Download size={14} className="shrink-0" />
                )}
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-xs font-bold">Scarica CSV</span>
                  <span className="text-[11px] text-gray-300 group-hover:text-white">{s.label}</span>
                </span>
              </span>
              <span className="text-[10px] bg-black/40 px-2 py-0.5 rounded-full font-mono shrink-0">
                {s.count}
              </span>
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExportDataPanel;
